import { SupabaseClient } from '@supabase/supabase-js';
import { Database } from '../../types/supabase';

// Tipos auxiliares
export type StreakStatus = {
  current_streak: number;
  longest_streak: number;
  last_activity_date: string | null;
  streak_protected: boolean;
  weekly_protection_available: boolean;
  protection_reset_date: string | null;
};

// 1. Função para registrar atividade qualificada e atualizar streak
export async function registerStreakActivity(
  supabase: SupabaseClient<Database>,
  userId: string,
  activityType: string
): Promise<{ success: boolean; status?: StreakStatus; error?: string }> {
  // Data de hoje (UTC)
  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10); // yyyy-mm-dd

  // Buscar streak atual
  const { data: streakArr, error: streakError } = await supabase
    .from('user_streaks')
    .select('*')
    .eq('user_id', userId)
    .limit(1);

  const streak = streakArr && streakArr.length > 0 ? streakArr[0] : null;

  let currentStreak = 0;
  let longestStreak = 0;
  let lastActivityDate: string | null = null;
  let streakProtected = false;
  let weeklyProtectionAvailable = true;
  let protectionResetDate: string | null = null;

  if (streak) {
    currentStreak = streak.current_streak;
    longestStreak = streak.longest_streak;
    lastActivityDate = streak.last_activity_date;
    streakProtected = streak.streak_protected;
    weeklyProtectionAvailable = streak.weekly_protection_available;
    protectionResetDate = streak.protection_reset_date;
  }

  // Calcular diferença de dias
  let daysDiff = 0;
  if (lastActivityDate) {
    const last = new Date(lastActivityDate);
    const diffMs = today.getTime() - last.getTime();
    daysDiff = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  }

  let newStreak = currentStreak;
  let newLongest = longestStreak;
  let usedProtection = false;

  if (!lastActivityDate || daysDiff === 1) {
    // Continuação da streak
    newStreak = currentStreak + 1;
  } else if (daysDiff === 0) {
    // Já registrou hoje, não faz nada
    return { success: true, status: {
      current_streak: currentStreak,
      longest_streak: longestStreak,
      last_activity_date: todayStr,
      streak_protected: streakProtected,
      weekly_protection_available: weeklyProtectionAvailable,
      protection_reset_date: protectionResetDate,
    }};
  } else if (daysDiff > 1) {
    // Streak quebrada
    if (weeklyProtectionAvailable) {
      // Usar proteção semanal
      newStreak = currentStreak + 1;
      usedProtection = true;
      weeklyProtectionAvailable = false;
      streakProtected = true;
      protectionResetDate = todayStr;
      // Registrar uso da proteção
      await supabase.from('streak_protection_log').insert({
        user_id: userId,
        protection_date: todayStr,
        protection_type: 'weekly',
      });
    } else {
      // Resetar streak
      newStreak = 1;
      streakProtected = false;
    }
  }

  if (newStreak > newLongest) {
    newLongest = newStreak;
  }

  // Upsert na tabela user_streaks
  const { error: upsertError } = await supabase
    .from('user_streaks')
    .upsert({
      user_id: userId,
      current_streak: newStreak,
      longest_streak: newLongest,
      last_activity_date: todayStr,
      streak_protected: streakProtected,
      weekly_protection_available: weeklyProtectionAvailable,
      protection_reset_date: protectionResetDate,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id' });

  if (upsertError) {
    return { success: false, error: 'Erro ao atualizar streak.' };
  }

  // Registrar no log de atividades
  await supabase.from('streak_activity_log').upsert({
    user_id: userId,
    activity_date: todayStr,
    activity_type: activityType,
    streak_day: newStreak,
    created_at: new Date().toISOString(),
  }, { onConflict: 'user_id,activity_date' });

  return {
    success: true,
    status: {
      current_streak: newStreak,
      longest_streak: newLongest,
      last_activity_date: todayStr,
      streak_protected: streakProtected,
      weekly_protection_available: weeklyProtectionAvailable,
      protection_reset_date: protectionResetDate,
    }
  };
}

// 2. Função para calcular o multiplicador de XP do streak
export function calculateStreakXpMultiplier(currentStreak: number): number {
  if (currentStreak >= 60) return 2.0;
  if (currentStreak >= 30) return 1.5;
  if (currentStreak >= 14) return 1.3;
  if (currentStreak >= 7) return 1.2;
  if (currentStreak >= 3) return 1.1;
  return 1.0;
}

// 3. Função para consultar status do streak do usuário
export async function getUserStreakStatus(
  supabase: SupabaseClient<Database>,
  userId: string
): Promise<StreakStatus | null> {
  const { data, error } = await supabase
    .from('user_streaks')
    .select('*')
    .eq('user_id', userId)
    .limit(1);

  if (error) {
    console.error('[getUserStreakStatus] Erro ao buscar streak:', error);
    return null;
  }
  if (!data || data.length === 0) return null;

  const row = data[0];
  return {
    current_streak: row.current_streak,
    longest_streak: row.longest_streak,
    last_activity_date: row.last_activity_date,
    streak_protected: row.streak_protected,
    weekly_protection_available: row.weekly_protection_available,
    protection_reset_date: row.protection_reset_date,
  };
}

// 4. Função para obter o multiplicador de XP do streak do usuário
export async function getUserStreakXpMultiplier(
  supabase: SupabaseClient<Database>,
  userId: string
): Promise<number> {
  const status = await getUserStreakStatus(supabase, userId);
  if (!status) return 1.0;
  return calculateStreakXpMultiplier(status.current_streak);
} 